import { useState, useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== Founder Fire Badge — random motivational tag, rotates =====
// Shows on dashboard home. New line every 45s + on every page refresh.
// Shannon's PERSONAL hype — separate from client-facing Cushman-tier copy.
export const FounderFireBadge = () => {
  const [line, setLine] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/founder-fire`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setLine(d.line);
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 180000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !line) return null;

  return (
    <div data-testid="founder-fire-badge" style={{
      background: "linear-gradient(135deg, #FF4D6D 0%, #FFB347 100%)",
      borderRadius: 12,
      padding: "14px 18px",
      marginBottom: 18,
      cursor: "pointer",
      transition: "transform 0.15s",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}
    onClick={load}
    title="Tap for a new founder-fire line"
    >
      <div style={{ fontSize: 28, lineHeight: 1 }}>🔥</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, color: "rgba(3,11,26,0.65)", fontWeight: 800, letterSpacing: "0.16em", marginBottom: 3 }}>FOUNDER FIRE</div>
        <div style={{ color: "#030B1A", fontSize: 15, fontWeight: 800, lineHeight: 1.35 }}>
          {line}
        </div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); load(); }} title="New fire" style={{ background: "rgba(3,11,26,0.20)", border: "none", color: "#030B1A", width: 28, height: 28, borderRadius: "50%", fontSize: 14, fontWeight: 800, cursor: "pointer" }} data-testid="founder-fire-refresh">↻</button>
    </div>
  );
};

// ===== Quick Capture FAB — floating bottom-right "drop a thought" button =====
// Voice-to-text mic + text input. Saves note with timestamp + optional project tag.
export const QuickCaptureFAB = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    r.onresult = (e) => {
      let finalT = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalT += e.results[i][0].transcript + " ";
      }
      if (finalT) setText(prev => (prev ? `${prev} ${finalT.trim()}` : finalT.trim()));
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recognitionRef.current = r;
  }, []);

  const toggleMic = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) { try { r.stop(); } catch {} setListening(false); }
    else { try { r.start(); setListening(true); } catch {} }
  };

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/field-notes`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ text, project_id: projectId, tags: [] })
      });
      if (r.ok) {
        setSaved(true);
        setText("");
        setProjectId("");
        setTimeout(() => { setSaved(false); setOpen(false); }, 1200);
      }
    } catch {}
    setSaving(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="quick-capture-fab"
        title="Drop a thought (Quick Capture)"
        style={{
          /* Bottom-right primary FAB. CockpitHelpPanel (?) stacks above
             this at bottom:96px. Ask Moovin lives on the left. */
          position: "fixed",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
          right: 24,
          width: 52,
          height: 52,
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #FFB347 0%, #FF4D6D 100%)",
          color: "#030B1A",
          fontSize: 22,
          fontWeight: 800,
          cursor: "pointer",
          boxShadow: "0 6px 20px rgba(255,77,109,0.45)",
          zIndex: 800,
        }}
      >
        ✎
      </button>
      {open && (
        <div data-testid="quick-capture-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(12px)", zIndex: 1700, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#0A1A2E", border: "1px solid rgba(255,179,71,0.30)", borderRadius: 14, padding: 22, maxWidth: 500, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 18 }}>✎ Quick Capture</h3>
              <button onClick={() => setOpen(false)} data-testid="quick-capture-close" style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ color: "#ADBED1", fontSize: 12, margin: "0 0 14px" }}>Drop a thought, todo, or field observation. Auto-timestamped. Findable later in Field Notes.</p>
            {saved ? (
              <div style={{ padding: 24, textAlign: "center", color: "#00DC78", fontSize: 14, fontWeight: 800 }}>✓ Saved to Field Notes</div>
            ) : (
              <>
                <div style={{ position: "relative", marginBottom: 10 }}>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="What did you just notice? Type or tap 🎤 to dictate..."
                    rows={5}
                    autoFocus
                    data-testid="quick-capture-text"
                    style={{ width: "100%", padding: "10px 44px 10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                  />
                  {recognitionRef.current && (
                    <button onClick={toggleMic} data-testid="quick-capture-mic" style={{ position: "absolute", right: 8, top: 8, width: 32, height: 32, borderRadius: "50%", border: "none", background: listening ? "#FF4D6D" : "rgba(0,255,255,0.18)", color: listening ? "#fff" : "#00FFFF", cursor: "pointer", fontSize: 14, animation: listening ? "mfPulse 1.2s infinite" : "none" }}>{listening ? "■" : "🎤"}</button>
                  )}
                </div>
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="quick-capture-project" style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, marginBottom: 12 }}>
                  <option value="">— Tag to project (optional) —</option>
                  {projects.map(p => (
                    <option key={p.project_id} value={p.project_id}>{p.address}{p.unit_number ? ` #${p.unit_number}` : ""}</option>
                  ))}
                </select>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setOpen(false)} style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                  <button onClick={save} disabled={saving || !text.trim()} data-testid="quick-capture-save" style={{ flex: 2, padding: "10px 14px", borderRadius: 8, border: "1px solid #FFB347", background: "rgba(255,179,71,0.18)", color: "#FFB347", fontSize: 13, fontWeight: 800, cursor: (saving || !text.trim()) ? "not-allowed" : "pointer", opacity: (saving || !text.trim()) ? 0.5 : 1 }}>{saving ? "Saving…" : "✎ Save Note"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ===== Field Notes Panel — list view (collapsible) =====
export const FieldNotesPanel = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/field-notes?limit=20`, { credentials: "include" });
      if (r.ok) setNotes(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    if (expanded) load();
  }, [expanded]);

  const deleteNote = async (note_id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/field-notes/${note_id}`, { method: "DELETE", credentials: "include" });
      if (r.ok) setNotes(notes.filter(n => n.note_id !== note_id));
    } catch {}
  };

  return (
    <div data-testid="field-notes-panel" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 14, marginBottom: 18 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <div>
          <div style={{ fontSize: 10, color: "#FFB347", fontWeight: 800, letterSpacing: "0.12em" }}>📓 FIELD NOTES</div>
          <div style={{ color: "#fff", fontSize: 14, fontWeight: 700, marginTop: 2 }}>Your captured thoughts ({notes.length})</div>
        </div>
        <span style={{ color: "#FFB347", fontSize: 14 }}>{expanded ? "▼" : "▶"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflowY: "auto" }}>
          {loading ? <div style={{ color: "#7E92A8", fontSize: 12, padding: 10 }}>Loading…</div> :
           notes.length === 0 ? <div style={{ color: "#7E92A8", fontSize: 12, padding: 10 }}>No notes yet. Tap the ✎ button (bottom-right) to drop your first thought.</div> :
           notes.map(n => (
            <div key={n.note_id} data-testid={`field-note-${n.note_id}`} style={{ padding: 10, borderRadius: 8, background: "rgba(255,179,71,0.06)", borderLeft: "3px solid #FFB347" }}>
              <div style={{ fontSize: 10, color: "#FFB347", fontWeight: 700, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{new Date(n.created_at).toLocaleString()} · {n.created_by}</span>
                <button onClick={() => deleteNote(n.note_id)} style={{ background: "transparent", border: "none", color: "#7E92A8", cursor: "pointer", fontSize: 11 }} title="Delete">✕</button>
              </div>
              <div style={{ color: "#fff", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{n.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
